// libs/ui/src/primitives/Input.tsx
import { forwardRef, useCallback, type ComponentProps } from 'react';
import type { TextInput } from 'react-native';
import { styled, Input as TamaguiInput } from 'tamagui';
import type { RNTextInputCompat, RNTextInputCompatKeys } from './rnTextInputCompat';
import { useRowFocusFieldContext } from '../hooks/useRowFocus';
import {
  DEFAULT_INPUT_VARIANT,
  INLINE_INPUT_VARIANT,
  ERROR_INPUT_VARIANT_TRUE,
  DISABLED_INPUT_VARIANT_TRUE,
} from './inputVariants';

const StyledInput = styled(TamaguiInput, {
  name: 'Input',
  color: '$textOnSurface',
  placeholderTextColor: '$textSubdued',
  borderRadius: 10,
  // 44pt WCAG 2.5.5 touch-target floor (ADR-0026 / ADR-0074). Previous
  // value was 40 — three known call sites may shift slightly in dense
  // edit rows; spot-check on contact-us, family, BlockRow.
  minHeight: 44,
  outlineWidth: 0,
  outlineStyle: 'none' as never,

  variants: {
    variant: {
      default: DEFAULT_INPUT_VARIANT,
      inline: INLINE_INPUT_VARIANT,
    },
    // Boolean variants compose with the active `variant` per ADR-0074:
    // `<Input error />` paints the border red, `<Input disabled />`
    // greys it out, the two stack if both are set.
    error: {
      true: ERROR_INPUT_VARIANT_TRUE,
    },
    disabled: {
      true: DISABLED_INPUT_VARIANT_TRUE,
    },
  } as const,
});

type StyledInputProps = ComponentProps<typeof StyledInput>;

export interface InputStateProps {
  /** Paints `$borderError`, sets `aria-invalid` / accessibilityState. */
  error?: boolean;
  /** Greys out the input, blocks focus, announces `aria-disabled`. */
  disabled?: boolean;
  /** `aria-describedby` target id — Phase 3 `FormRow` populates this. */
  describedById?: string;
}

export type InputProps = Omit<StyledInputProps, RNTextInputCompatKeys> &
  InputStateProps &
  RNTextInputCompat;

/**
 * App-wide text input. Every text field in the app — Stay name, Journey
 * carrier, Schedule date/time, trip-panel inputs — routes through this
 * primitive (directly or via `FormInput` / `MaskedInput` / `TimeInput`)
 * so focus and outline behavior stay consistent.
 *
 * State props (ADR-0074): `error`, `disabled`, and `describedById` paint
 * Phase 1 tokens and propagate ARIA attributes. When the parent is a
 * `FormRow` (ADR-0075), these props are populated automatically via
 * `RowFocusContext` — explicit props still win.
 *
 * Focus reporting: any parent that mounts a `RowFocusContext.Provider`
 * (FormRow, FieldRow, PanelFieldRow) receives focus/blur
 * events from this input. That row toggles its own focus border, and
 * the input itself stays borderless under the `inline` variant.
 *
 * Defaults autofill OFF so every numeric / search / note input doesn't
 * get iOS Safari's "passwords / credit cards / addresses" toolbar
 * (the QuickType bar) above the keyboard. Credential inputs (login,
 * signup) opt back IN by passing `autoComplete="email"` /
 * `autoComplete="current-password"` — the spread below lets caller
 * props win.
 */
type InputFocusHandler = NonNullable<StyledInputProps['onFocus']>;
type InputBlurHandler = NonNullable<StyledInputProps['onBlur']>;
type InputFocusEvent = Parameters<InputFocusHandler>[0];
type InputBlurEvent = Parameters<InputBlurHandler>[0];

// eslint-disable-next-line @typescript-eslint/no-shadow -- the inner function expression is deliberately named after the const so React DevTools and stack traces show the component name; it is not a real shadow.
export const Input = forwardRef<TextInput, InputProps>(function Input(props, ref) {
  const fieldContext = useRowFocusFieldContext();
  const { onFocus, onBlur, variant, error, disabled, describedById, id, editable, ...rest } =
    props;

  // FormRow context wins as a fallback — explicit props still override.
  // - `error` and `disabled` flip the styled variants on.
  // - `describedById` becomes the aria-describedby target.
  // - `id` defaults to the FormRow's controlId so <label htmlFor> ties up.
  const resolvedError = error ?? fieldContext.error;
  const resolvedDisabled = disabled ?? fieldContext.disabled;
  const resolvedDescribedBy = describedById ?? fieldContext.messageId;
  const resolvedId = id ?? fieldContext.controlId;

  // If a parent row provides a focus reporter, this input is row-embedded
  // and the row owns the focus border — so default to `inline`. Otherwise
  // it's standalone and renders its own focus border. An explicit
  // `variant` prop always wins.
  const reportRowFocus = fieldContext.reporter;
  const resolvedVariant = variant ?? (reportRowFocus ? 'inline' : 'default');

  const handleFocus = useCallback(
    (e: InputFocusEvent) => {
      reportRowFocus?.(true);
      onFocus?.(e);
    },
    [reportRowFocus, onFocus],
  );

  const handleBlur = useCallback(
    (e: InputBlurEvent) => {
      reportRowFocus?.(false);
      onBlur?.(e);
    },
    [reportRowFocus, onBlur],
  );

  // Consolidate every prop into a single object so we can cast it once —
  // Tamagui's typed props don't compose cleanly with exactOptionalPropertyTypes.
  const allProps: Record<string, unknown> = {
    autoComplete: 'off',
    autoCorrect: false,
    spellCheck: false,
    textContentType: 'none',
    importantForAutofill: 'no',
    ...rest,
    id: resolvedId,
    variant: resolvedVariant,
    error: resolvedError,
    disabled: resolvedDisabled,
    // Tamagui v2 derives editability from `disabled` / `readOnly` and
    // ignores a bare `editable`. `disabled` already blocks input; map an
    // explicit `editable={false}` (read-only, not greyed) onto `readOnly`.
    ...(editable === false ? { readOnly: true } : null),
    ...(resolvedError ? { 'aria-invalid': true } : null),
    ...(resolvedDisabled ? { 'aria-disabled': true } : null),
    ...(resolvedDescribedBy ? { 'aria-describedby': resolvedDescribedBy } : null),
    accessibilityState: { disabled: !!resolvedDisabled },
    onFocus: handleFocus,
    onBlur: handleBlur,
    ref,
  };
  return <StyledInput {...allProps} />;
});
